/**
 * Modal and ConfirmDialog.
 *
 * The modal is where configuration lives. Rules, policies, scripts, histories, rate cards
 * and breakdowns are all one click off the page, and this is the click.
 *
 * ONE SHELL. There are two widths — 640 for a decision or a form, 860 when the body is
 * verbatim text, a table or a chart — one close control, and one footer grammar:
 *
 *     [primary] [secondary] ·············· [tertiary link]
 *
 * Four widths and four footer orders is what "twelve screens by different people" looks
 * like from ten feet away. `narrow` and `full` remain as size names so no screen has to be
 * edited, but they resolve onto the two real widths and cannot reintroduce a third.
 *
 * ConfirmDialog exists because "Are you sure?" is not a confirmation. A confirmation says
 * exactly what will happen, in the operator's terms, before it happens:
 *
 *     Re-confirm the outage announcement
 *       · The announcement stays live for another 6 hours, until 5:52 AM.
 *       · Callers in Sugar Grove keep hearing script v4 exactly as it is now.
 *       · Nothing is sent to any customer, no one is paged, the ETA field stays empty.
 *       · The re-confirmation is recorded against your name.
 *
 * `wakesPerson` is its own emphasis for a reason: this product's whole promise is that it
 * does not wake people unnecessarily. When an operator is about to do it anyway, the dialog
 * says so in the critical hue, names the person, and states the time — because at 11:52 PM
 * the relevant question is not "are you sure" but "does this still need a human tonight".
 *
 * Note the raw <button> elements below: Button.tsx renders a ConfirmDialog, so this file
 * must not import Button or the two would be circular.
 */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { useBodyScrollLock, useEscapeKey, useFocusTrap, useOverlayId, useScrimHandler } from './useOverlay.tsx';

export type ModalSize = 'narrow' | 'default' | 'wide' | 'full';

export type ModalProps = {
  open: boolean;
  title: string;
  /** Small print under the title — usually the record this dialog is about. */
  subtitle?: ReactNode;
  onClose: () => void;
  /**
   * The footer row. One grammar: primary first, then secondary, then an optional tertiary
   * text link, which the stylesheet pushes to the right edge on its own. Omit it entirely
   * and the dialog renders a single Close — never no footer at all.
   */
  footer?: ReactNode;
  /** 640 by default; 'wide' is 860 and is for verbatim text, a table or a chart. */
  size?: ModalSize;
  /** Escape hatch for an exact width. Prefer `size`. */
  width?: number;
  /** 'none' when the body is a table or a full-bleed list. */
  padding?: 'normal' | 'none';
  /** Extra controls in the header, left of the close button. */
  headerActions?: ReactNode;
  children: ReactNode;
  /** Set for a dialog mid-submit so a stray Escape cannot orphan the request. */
  busy?: boolean;
  /** Label for the close button, when "Close dialog" is not specific enough. */
  closeLabel?: string;
};

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  footer,
  size = 'default',
  width,
  padding = 'normal',
  headerActions,
  children,
  busy = false,
  closeLabel,
}: ModalProps): ReactElement | null {
  const ref = useRef<HTMLDivElement>(null);
  const id = useOverlayId('modal');
  const onScrim = useScrimHandler(() => {
    if (!busy) onClose();
  });

  useEscapeKey(open && !busy, onClose);
  useBodyScrollLock(open);
  // The trap also restores focus to whatever opened the dialog when it unmounts.
  useFocusTrap(open, ref);

  if (!open) return null;

  return createPortal(
    <div className="nd-modal-layer" onMouseDown={onScrim}>
      <div
        ref={ref}
        className="nd-modal"
        data-size={size === 'default' ? undefined : size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={subtitle ? `${id}-sub` : undefined}
        style={width ? ({ width: `${width}px` } as CSSProperties) : undefined}
      >
        <div className="nd-modal-head">
          <div style={{ minWidth: 0 }}>
            <h3 id={`${id}-title`}>{title}</h3>
            {subtitle ? (
              <div className="nd-modal-sub" id={`${id}-sub`}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <div className="nd-right">
            {headerActions}
            <button
              type="button"
              className="nd-iconbtn nd-modal-close"
              data-nd-close="true"
              aria-label={closeLabel ?? 'Close dialog'}
              title={closeLabel ?? 'Close'}
              onClick={onClose}
              disabled={busy}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>

        <div className="nd-modal-body" data-pad={padding === 'none' ? 'none' : undefined}>
          {children}
        </div>

        {/*
          Every dialog has a footer. One that ends on its last content row and then stops at
          the card edge reads unfinished, and it leaves the header ✕ as the only way out —
          which is the one control a dialog should never make you hunt for.
        */}
        <div className="nd-modal-foot">
          {footer ?? (
            <button type="button" className="nd-btn" onClick={onClose} disabled={busy}>
              {closeLabel ?? 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------------ confirm

export type ConfirmSpec = {
  title: string;
  /** Small print under the title — the record, the current state. */
  subtitle?: string;
  /** One sentence of context. Optional — the consequences usually say enough. */
  body?: string;
  /**
   * What will actually happen, one clause per line. Required: a confirm dialog that
   * cannot list its consequences is a confirm dialog that has not thought about them.
   */
  consequences: string[];
  /**
   * Typed facts about the action: who, how, which number, what happens if no answer.
   * Rendered as the key/value list from the approved sheet.
   */
  details?: { label: ReactNode; value: ReactNode; key?: string }[];
  /**
   * THE emphasis. Pass the name of the person whose phone is about to ring. Renders the
   * critical callout: their phone rings at full volume, overriding do-not-disturb, and the
   * dialog says what time it is so the operator has to think about that before clicking.
   */
  wakesPerson?: string;
  /** Extra line for the wake callout — the time, the escalation that follows. */
  wakesDetail?: string;
  /** Doctrine 7: what demo mode will do to this action instead of doing it. */
  demoNote?: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** Renders the "This cannot be undone." line. */
  irreversible?: boolean;
  /**
   * Type-to-confirm for the genuinely unrecoverable. The operator must type this string
   * exactly. Use it sparingly; on a night shift it is friction, and friction everywhere
   * is friction nowhere.
   */
  typeToConfirm?: string;
};

export type ConfirmDialogProps = {
  open: boolean;
  spec: ConfirmSpec;
  /**
   * Receives what the operator actually typed into the type-to-confirm box (empty string
   * when the spec does not ask for one). Handlers that send a confirmation string to the
   * server must forward THIS, not the value they already hold — a client that fills in the
   * server's own check has not been checked by anything.
   */
  onConfirm: (typed: string) => void;
  onCancel: () => void;
  busy?: boolean;
};

export function ConfirmDialog({
  open,
  spec,
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDialogProps): ReactElement | null {
  const typedRef = useRef<HTMLInputElement>(null);
  const wakes = Boolean(spec.wakesPerson);
  const tone = spec.tone ?? (wakes ? 'danger' : 'default');
  const needsTyping = Boolean(spec.typeToConfirm);

  const confirm = (): void => {
    const typed = typedRef.current?.value.trim() ?? '';
    if (needsTyping && typed !== spec.typeToConfirm) return;
    onConfirm(typed);
  };

  return (
    <Modal
      open={open}
      title={spec.title}
      subtitle={spec.subtitle}
      onClose={onCancel}
      busy={busy}
      size="narrow"
      footer={
        <>
          <button
            type="button"
            className="nd-btn"
            data-variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={confirm}
            disabled={busy}
            data-autofocus={needsTyping ? undefined : 'true'}
          >
            {busy ? <span className="nd-btn-spin" aria-hidden="true" /> : null}
            {spec.confirmLabel}
          </button>
          <button type="button" className="nd-btn" data-variant="ghost" onClick={onCancel} disabled={busy}>
            {spec.cancelLabel ?? 'Cancel'}
          </button>
        </>
      }
    >
      {wakes ? (
        <div className="nd-callout" data-tone="danger">
          <Icon name="bell" size={16} />
          <span>
            <b>This wakes a person.</b> {spec.wakesPerson}
            {spec.wakesPerson?.endsWith('.') ? '' : '.'}
            {spec.wakesDetail ? ` ${spec.wakesDetail}` : ''}
          </span>
        </div>
      ) : null}

      <div className="nd-callout" data-tone={tone === 'danger' && !wakes ? 'danger' : undefined} style={wakes ? { marginTop: 12 } : undefined}>
        <Icon name="shield" size={16} />
        <span>
          <b>What will happen.</b>{' '}
          {spec.body ? <>{spec.body} </> : null}
          {spec.consequences.join(' ')}
          {spec.irreversible ? ' This cannot be undone.' : ''}
        </span>
      </div>

      {spec.details && spec.details.length > 0 ? (
        <dl className="nd-kv" style={{ marginTop: 16 }}>
          {spec.details.map((row, index) => (
            <div key={row.key ?? index} style={{ display: 'contents' }}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {spec.demoNote ? (
        <div className="nd-modal-note" style={{ marginTop: 14 }}>
          <Icon name="lock" size={13} />
          <span>{spec.demoNote}</span>
        </div>
      ) : null}

      {needsTyping ? (
        <label className="nd-field" style={{ marginTop: 16, display: 'block' }}>
          <span className="nd-label">
            Type <code>{spec.typeToConfirm}</code> to confirm
          </span>
          <input
            ref={typedRef}
            className="nd-input"
            data-mono="true"
            data-autofocus="true"
            autoComplete="off"
            spellCheck={false}
            onKeyDown={(event) => {
              if (event.key === 'Enter') confirm();
            }}
          />
        </label>
      ) : null}
    </Modal>
  );
}

export default Modal;
